/**
 * Temperature Role - Constants
 *
 * Fórmulas de conversão, símbolos e aliases para unidades de temperatura.
 *
 * IMPORTANTE: Temperatura usa FÓRMULAS, não fatores multiplicativos.
 * Isso porque as escalas têm offsets diferentes (0°C ≠ 0°F ≠ 0K).
 *
 * Fontes:
 * - NIST SP 811 (2008)
 * - SI Brochure, 9th Edition (2019)
 */

import type { SimpleUnitConfig, UnitAliases } from "../../contracts";

// =============================================================================
// TYPES
// =============================================================================

export type TemperatureUnit = "celsius" | "fahrenheit" | "kelvin" | "rankine";

// =============================================================================
// UNITS CONFIG
// =============================================================================

/**
 * Configuração de cada unidade de temperatura.
 *
 * Base: celsius (°C)
 *
 * Fórmulas de conversão:
 * - Celsius ↔ Fahrenheit: °F = °C × 9/5 + 32
 * - Celsius ↔ Kelvin: K = °C + 273.15
 * - Celsius ↔ Rankine: °R = (°C + 273.15) × 9/5
 *
 * Pontos de referência:
 * | Descrição        | Celsius | Fahrenheit | Kelvin  | Rankine |
 * |------------------|---------|------------|---------|---------|
 * | Zero absoluto    | -273.15 | -459.67    | 0       | 0       |
 * | Congelamento H₂O | 0       | 32         | 273.15  | 491.67  |
 * | Corpo humano     | 37      | 98.6       | 310.15  | 558.27  |
 * | Ebulição H₂O     | 100     | 212        | 373.15  | 671.67  |
 */
export const TEMPERATURE_UNITS: Record<TemperatureUnit, SimpleUnitConfig> = {
  celsius: {
    symbol: "°C",
    singular: "degree Celsius",
    plural: "degrees Celsius",
    toBase: (c: number) => c,
    fromBase: (c: number) => c,
    noSpace: true, // 25°C não 25 °C
  },
  fahrenheit: {
    symbol: "°F",
    singular: "degree Fahrenheit",
    plural: "degrees Fahrenheit",
    toBase: (f: number) => (f - 32) * (5 / 9),
    fromBase: (c: number) => c * (9 / 5) + 32,
    noSpace: true, // 77°F não 77 °F
  },
  kelvin: {
    symbol: "K",
    singular: "kelvin",
    plural: "kelvins",
    toBase: (k: number) => k - 273.15,
    fromBase: (c: number) => c + 273.15,
    // Kelvin usa espaço: "273 K" (convenção SI)
  },
  rankine: {
    symbol: "°R",
    singular: "degree Rankine",
    plural: "degrees Rankine",
    toBase: (r: number) => (r - 491.67) * (5 / 9),
    fromBase: (c: number) => (c + 273.15) * (9 / 5),
    noSpace: true, // 500°R não 500 °R
  },
};

// =============================================================================
// ALIASES
// =============================================================================

/**
 * Aliases para reconhecimento de unidades no cast
 */
export const TEMPERATURE_ALIASES: UnitAliases = {
  // celsius
  c: "celsius",
  "°c": "celsius",
  // fahrenheit
  f: "fahrenheit",
  "°f": "fahrenheit",
  // kelvin
  k: "kelvin",
  // rankine
  r: "rankine",
  "°r": "rankine",
};

// =============================================================================
// HELPERS
// =============================================================================

/** Lista de todas as unidades */
export const TEMPERATURE_UNIT_LIST = Object.keys(
  TEMPERATURE_UNITS,
) as TemperatureUnit[];

/**
 * Constantes importantes
 */
export const TEMPERATURE_CONSTANTS = {
  /** Zero absoluto em Celsius */
  ABSOLUTE_ZERO_CELSIUS: -273.15,
  /** Zero absoluto em Fahrenheit */
  ABSOLUTE_ZERO_FAHRENHEIT: -459.67,
  /** Offset Celsius → Kelvin */
  CELSIUS_TO_KELVIN_OFFSET: 273.15,
  /** Offset Fahrenheit → Rankine (são a mesma escala, só diferente offset) */
  FAHRENHEIT_TO_RANKINE_OFFSET: 459.67,
  /** Ponto de congelamento da água em Fahrenheit */
  WATER_FREEZING_FAHRENHEIT: 32,
  /** Ponto de ebulição da água em Fahrenheit */
  WATER_BOILING_FAHRENHEIT: 212,
} as const;
